import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Flatpickr from "react-flatpickr";
import {
  Badge,
  Col,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import {
  useGetCourseDurationsQuery,
  useUpdateCourseDurationMutation,
} from "../../store/api/courseDurationApi";
import { PaginateQuery, UpdateCourseDurationRequest } from "../../interfaces";
import { CourseDurationDto } from "../../interfaces/course";
import { ActivityButton } from "../Common";

type TProps = {
  courseId: string;
  gradeId: string;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const CourseDurationModal: React.FC<TProps> = ({
  courseId,
  gradeId,
  show,
  onCloseClick,
}) => {
  //#region hooks
  const { t } = useTranslation();
  //#endregion

  //#region internal state
  const initState = new PaginateQuery();
  const [paginateQuery] = useState<PaginateQuery>({
    ...initState,
    limit: 1000,
  });

  const [addUpdateRequest, setAddUpdateRequest] = useState<
    UpdateCourseDurationRequest[]
  >([]);
  const [updateError, setUpdateError] = useState<string | null>(null);
  //#endregion

  //#region RTK Query
  const { data: courseDurations, isLoading: isLoadingCourseDurations } =
    useGetCourseDurationsQuery(
      { gradeId, courseId, data: paginateQuery },
      { skip: !courseId || !gradeId || !show },
    );

  const [updateCourseDuration, { isLoading: isLoadingUpdate }] =
    useUpdateCourseDurationMutation();
  //#endregion

  //#region effects
  useEffect(() => {
    if (!show) {
      setAddUpdateRequest([]);
    }
  }, [courseId, gradeId, show]);

  useEffect(() => {
    if (courseDurations && courseDurations.data) {
      const { data } = courseDurations;
      setAddUpdateRequest(
        data.map((item: CourseDurationDto): UpdateCourseDurationRequest => {
          return {
            id: item.id,
            courseId: item.course.id,
            gradeId: item.grade.id,
            startAt: item.startAt,
            endAt: item.endAt,
          };
        }),
      );
    } else {
      setAddUpdateRequest([]);
    }
  }, [courseDurations, isLoadingCourseDurations, courseId, gradeId, show]);

  //#endregion

  //#region handlers

  const onClose = () => {
    onCloseClick(!show);
  };

  const onChangeDateRange = (event: any, index: number) => {
    addUpdateRequest[index].startAt = event[0];
    addUpdateRequest[index].endAt = event[1];
    setAddUpdateRequest([...addUpdateRequest]);
  };

  const onSave = async () => {
    if (isValid()) {
      try {
        setUpdateError(null);
        await updateCourseDuration(addUpdateRequest).unwrap();
        onClose();
      } catch (err: any) {
        setUpdateError(err?.data?.message || "An error occurred");
      }
    } else {
      setUpdateError(t("durationRequired"));
    }
  };

  const isValid = () => {
    return addUpdateRequest.every((item) => item.startAt && item.endAt);
  };

  const onStartNew = () => {
    setAddUpdateRequest([
      {
        courseId: courseId,
        gradeId: gradeId,
      },
      ...addUpdateRequest,
    ]);
  };

  //#endregion

  return (
    <Modal
      id="addEdit"
      isOpen={show}
      toggle={() => {
        onClose();
      }}
      className="modal-md modal-dialog-scrollable"
      centered
    >
      <ModalHeader
        className="p-3"
        toggle={() => {
          onClose();
        }}
      >
        {t("editItem", { item: "semesterGradeDuration" })}
      </ModalHeader>
      <ModalBody>
        <Row className="mb-3">
          <Col>
            {typeof updateError === "string" && (
              <Label className="invalid-feedback d-block">{updateError}</Label>
            )}
          </Col>
          <Col className="text-end">
            <ActivityButton
              title="startNew"
              inProgressTitle="saving"
              color="success"
              className="btn btn-light"
              type="submit"
              inProgress={false}
              onClick={onStartNew}
            ></ActivityButton>
            &nbsp;
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              onClick={onSave}
              inProgress={isLoadingUpdate}
              disabled={isLoadingUpdate}
            ></ActivityButton>
          </Col>
        </Row>
        <Row>
          <Col className="mb-3">
            <span>{t("instructions")}</span>
            <ul>
              <li>{t("courseDurationInstructions1")}</li>
              <li>{t("courseDurationInstructions2")}</li>
              <li>{t("courseDurationInstructions3")}</li>
            </ul>
          </Col>
        </Row>
        {addUpdateRequest &&
          addUpdateRequest.map(
            (item: UpdateCourseDurationRequest, key: number) => {
              return (
                <Row key={key}>
                  <Col>
                    <div className="mb-3">
                      <div>
                        <label htmlFor="name.en" className="form-label">
                          {t("from")} - {t("to")}{" "}
                          <span className="text-danger">*</span>
                          {!item.id && (
                            <Badge color="secondary">{t("new")} </Badge>
                          )}
                        </label>
                      </div>
                      <Flatpickr
                        onChange={(e) => onChangeDateRange(e, key)}
                        className="form-control"
                        value={[item.startAt || "", item.endAt || ""]}
                        options={{
                          mode: "range",
                          dateFormat: "Y/m/d",
                        }}
                      />
                    </div>
                  </Col>
                </Row>
              );
            },
          )}
      </ModalBody>
    </Modal>
  );
};
